import {
  createContext,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type HTMLAttributes,
} from 'react';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
} from 'antd';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEstadoDeLista } from '@titularis/ui';
import { api } from '../api';
import type { OpcaoSimples } from '../components/SearchBoxCriavel';

/**
 * Cadastros auxiliares: bancas, cartórios e a taxonomia de três níveis.
 *
 * Eles nascem dentro das caixas de busca, no meio de outro cadastro - e é ali
 * que nome errado e duplicata aparecem. Esta tela existe para corrigir isso:
 * é onde se apaga o que foi criado por engano.
 *
 * Excluir o que ainda está em uso é recusado pelo banco, e a mensagem diz o
 * porquê. A ordem é sempre da folha para a raiz: delimitação, assunto e só
 * então a disciplina. Assuntos e delimitações têm ordem própria, arrastável.
 */
export function CadastrosPage() {
  const { ler, filtrar } = useEstadoDeLista();

  // A aba na URL: "manda o link do cadastro de bancas" precisa abrir em bancas.
  const aba = ler.texto('aba') ?? 'boards';

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Cadastros
        </Typography.Title>
        <Typography.Text type="secondary">
          Bancas, cartórios e a classificação das questões. Aqui se corrige o que foi criado por
          engano nas caixas de busca.
        </Typography.Text>
      </div>

      <Tabs
        activeKey={aba}
        // Trocar de aba zera a seleção da taxonomia, que só vale dentro dela.
        onChange={(chave) =>
          filtrar({
            aba: chave === 'boards' ? undefined : chave,
            disciplina: undefined,
            assunto: undefined,
          })
        }
        items={[
          { key: 'boards', label: 'Bancas', children: <ListaDeBancas /> },
          { key: 'notary', label: 'Cartórios', children: <ListaDeCartorios /> },
          { key: 'taxonomy', label: 'Disciplinas e assuntos', children: <Taxonomia /> },
        ]}
      />
    </Space>
  );
}

/* ------------------------------------------------- arrastar e soltar */

/**
 * A alça (grip) leva os listeners de arraste por context; só ela inicia o
 * arraste, então o resto da linha continua clicável (selecionar, renomear,
 * excluir). É o padrão de "drag handle" do dnd-kit com o AntD Table.
 */
interface ContextoDaAlca {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: DraggableSyntheticListeners;
}
const AlcaContext = createContext<ContextoDaAlca>({});

function AlcaDeArraste() {
  const { setActivatorNodeRef, listeners } = useContext(AlcaContext);
  return (
    <Button
      type="text"
      size="small"
      aria-label="Arrastar para reordenar"
      icon={<HolderOutlined />}
      style={{ cursor: 'grab', touchAction: 'none' }}
      ref={setActivatorNodeRef}
      onClick={(evento) => evento.stopPropagation()}
      {...listeners}
    />
  );
}

function LinhaArrastavel(props: HTMLAttributes<HTMLTableRowElement> & { 'data-row-key': string }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: props['data-row-key'] });

  const style: CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 2 } : {}),
  };

  const contexto = useMemo<ContextoDaAlca>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <AlcaContext.Provider value={contexto}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </AlcaContext.Provider>
  );
}

/** Lista simples com busca, criação e exclusão. Serve a todos os cadastros. */
function ListaSimples({
  titulo,
  queryKey,
  buscar,
  criar,
  renomear,
  excluir,
  onReordenar,
  numerada,
  prefixoNumero = '',
  vazio,
  selecionado,
  onSelecionar,
  desabilitado,
}: {
  titulo: string;
  queryKey: readonly unknown[];
  buscar: (termo: string | undefined) => Promise<OpcaoSimples[]>;
  criar: (nome: string) => Promise<OpcaoSimples>;
  renomear: (id: string, nome: string) => Promise<OpcaoSimples>;
  excluir: (id: string) => Promise<unknown>;
  /** Quando presente, a lista pode ser reordenada arrastando as linhas. */
  onReordenar?: (orderedIds: string[]) => Promise<unknown>;
  /** Mostra o número de ordem (1, 2...) à esquerda de cada item. */
  numerada?: boolean;
  /** Prefixo do número, para a delimitação herdar o número do assunto (ex.: "2."). */
  prefixoNumero?: string;
  vazio: string;
  selecionado?: string;
  onSelecionar?: (id: string) => void;
  desabilitado?: boolean;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [termo, setTermo] = useState('');
  const [novo, setNovo] = useState('');
  // Qual linha está em edição e o texto sendo digitado nela.
  const [editando, setEditando] = useState<{ id: string; nome: string }>();

  const itens = useQuery({
    queryKey: [...queryKey, termo],
    queryFn: () => buscar(termo || undefined),
    enabled: !desabilitado,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey });

  const cadastrar = useMutation({
    mutationFn: (nome: string) => criar(nome),
    onSuccess: async () => {
      setNovo('');
      await invalidar();
      message.success(`${titulo} cadastrado.`);
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const editar = useMutation({
    mutationFn: ({ id, nome }: { id: string; nome: string }) => renomear(id, nome),
    onSuccess: async () => {
      setEditando(undefined);
      await invalidar();
      message.success('Nome atualizado.');
    },
    // Nome repetido volta como conflito (409): a mensagem da API explica.
    onError: (erro: Error) => message.error(erro.message),
  });

  const salvarEdicao = () => {
    const nome = editando?.nome.trim();
    if (!editando || !nome) return;
    editar.mutate({ id: editando.id, nome });
  };

  const apagar = useMutation({
    mutationFn: (id: string) => excluir(id),
    onSuccess: async () => {
      await invalidar();
      message.success('Excluído.');
    },
    // A recusa por estar em uso chega aqui: a mensagem da API já explica o que
    // precisa sair antes.
    onError: (erro: Error) => message.error(erro.message),
  });

  const reordenar = useMutation({
    mutationFn: (ids: string[]) => onReordenar!(ids),
    // Otimista: a nova ordem já vale na tela; se falhar, revalida e avisa.
    onError: async (erro: Error) => {
      message.error(erro.message);
      await invalidar();
    },
  });

  // Só arrasta a lista inteira e ordenada - não faz sentido reordenar um
  // resultado de busca (é um subconjunto).
  const arrastar = Boolean(onReordenar) && !termo;
  // O número reflete a ordem; numa busca (subconjunto) ele mentiria, então some.
  const mostrarNumero = Boolean(numerada) && !termo;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const aoArrastar = (evento: DragEndEvent) => {
    const { active, over } = evento;
    if (!over || active.id === over.id) return;

    const lista = itens.data ?? [];
    const de = lista.findIndex((item) => item.id === active.id);
    const para = lista.findIndex((item) => item.id === over.id);
    if (de < 0 || para < 0) return;

    const nova = arrayMove(lista, de, para);
    queryClient.setQueryData([...queryKey, termo], nova);
    reordenar.mutate(nova.map((item) => item.id));
  };

  const tabela = (
    <Table<OpcaoSimples>
      rowKey="id"
      size="small"
      showHeader={false}
      loading={itens.isPending}
      dataSource={itens.data ?? []}
      // Ao reordenar, mostra tudo: arrastar entre páginas não funcionaria.
      pagination={arrastar ? false : { pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
      components={arrastar ? { body: { row: LinhaArrastavel } } : undefined}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={vazio} /> }}
      rowClassName={(item) => (item.id === selecionado ? 'ant-table-row-selected' : '')}
      onRow={(item) => ({
        style: onSelecionar ? { cursor: 'pointer' } : undefined,
        onClick: () => onSelecionar?.(item.id),
      })}
      columns={[
        ...(arrastar ? [{ title: '', key: 'alca', width: 40, render: () => <AlcaDeArraste /> }] : []),
        {
          title: 'Nome',
          dataIndex: 'name',
          render: (nome: string, item, index) => {
            // Em edição o campo ocupa a célula inteira - sem número na frente.
            if (editando?.id === item.id) {
              return (
                <Input
                  autoFocus
                  size="small"
                  value={editando.nome}
                  disabled={editar.isPending}
                  aria-label={`Novo nome de ${nome}`}
                  onClick={(evento) => evento.stopPropagation()}
                  onChange={(evento) => setEditando({ id: item.id, nome: evento.target.value })}
                  onPressEnter={salvarEdicao}
                  onKeyDown={(evento) => evento.key === 'Escape' && setEditando(undefined)}
                />
              );
            }

            if (!mostrarNumero) return <span>{nome}</span>;
            return (
              // Flex, e não Space: o número não pode encolher nem quebrar ("1.1"
              // virava "1 . 1" na coluna estreita); o nome fica com o resto.
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
                <Typography.Text
                  type="secondary"
                  style={{ flex: 'none', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                >
                  {`${prefixoNumero}${index + 1}`}
                </Typography.Text>
                <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{nome}</span>
              </div>
            );
          },
        },
        {
          title: '',
          key: 'acoes',
          width: 92,
          align: 'right',
          render: (_, item) =>
            editando?.id === item.id ? (
              <Space size={0}>
                <Button
                  type="text"
                  size="small"
                  aria-label="Salvar nome"
                  icon={<CheckOutlined />}
                  loading={editar.isPending}
                  onClick={(evento) => {
                    evento.stopPropagation();
                    salvarEdicao();
                  }}
                />
                <Button
                  type="text"
                  size="small"
                  aria-label="Cancelar edição"
                  icon={<CloseOutlined />}
                  onClick={(evento) => {
                    evento.stopPropagation();
                    setEditando(undefined);
                  }}
                />
              </Space>
            ) : (
              <Space size={0}>
                <Button
                  type="text"
                  size="small"
                  aria-label={`Renomear ${item.name}`}
                  icon={<EditOutlined />}
                  onClick={(evento) => {
                    evento.stopPropagation();
                    setEditando({ id: item.id, nome: item.name });
                  }}
                />
                <Popconfirm
                  title={`Excluir ${item.name}?`}
                  okText="Excluir"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancelar"
                  onConfirm={() => apagar.mutate(item.id)}
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    aria-label={`Excluir ${item.name}`}
                    icon={<DeleteOutlined />}
                    onClick={(evento) => evento.stopPropagation()}
                  />
                </Popconfirm>
              </Space>
            ),
        },
      ]}
    />
  );

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={novo}
          disabled={desabilitado}
          placeholder={`Cadastrar ${titulo.toLowerCase()}`}
          onChange={(evento) => setNovo(evento.target.value)}
          onPressEnter={() => novo.trim() && cadastrar.mutate(novo.trim())}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={cadastrar.isPending}
          disabled={desabilitado || !novo.trim()}
          // Na taxonomia há três botões "Cadastrar" lado a lado: sem o rótulo
          // acessível, quem usa leitor de tela não sabe qual é qual.
          aria-label={`Cadastrar ${titulo.toLowerCase()}`}
          onClick={() => cadastrar.mutate(novo.trim())}
        >
          Cadastrar
        </Button>
      </Space.Compact>

      <Input.Search
        allowClear
        disabled={desabilitado}
        placeholder="Buscar"
        onSearch={setTermo}
        onChange={(evento) => !evento.target.value && setTermo('')}
      />

      {arrastar ? (
        <DndContext sensors={sensors} onDragEnd={aoArrastar}>
          <SortableContext
            items={(itens.data ?? []).map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {tabela}
          </SortableContext>
        </DndContext>
      ) : (
        tabela
      )}
    </Space>
  );
}

function ListaDeBancas() {
  return (
    <ListaSimples
      titulo="Banca"
      queryKey={['boards']}
      vazio="Nenhuma banca cadastrada."
      buscar={async (termo) => (await api.listBoards(termo)).items}
      criar={(nome) => api.createBoard(nome)}
      renomear={(id, nome) => api.renameBoard(id, nome)}
      excluir={(id) => api.deleteBoard(id)}
    />
  );
}

function ListaDeCartorios() {
  return (
    <ListaSimples
      titulo="Cartório"
      queryKey={['notary-offices']}
      vazio="Nenhum cartório cadastrado."
      buscar={async (termo) => (await api.listNotaryOffices(termo)).items}
      criar={(nome) => api.createNotaryOffice(nome)}
      renomear={(id, nome) => api.renameNotaryOffice(id, nome)}
      excluir={(id) => api.deleteNotaryOffice(id)}
    />
  );
}

/** Os três níveis lado a lado: escolher um nível abre o seguinte. */
function Taxonomia() {
  const { ler, filtrar } = useEstadoDeLista();

  /*
   * Escopo da taxonomia (D-18): a que concurso ou catálogo esta árvore
   * pertence. Fica acima da cascata, e não como quarta coluna - a proporção
   * 2:3:3 das três já foi ajustada uma vez por aperto horizontal.
   */
  const escopos = useQuery({ queryKey: ['scopes'], queryFn: () => api.listScopes() });
  const escopoDaUrl = ler.texto('escopo');
  const escopoId = escopoDaUrl ?? escopos.data?.[0]?.id;

  /*
   * Disciplina e assunto escolhidos vão para a URL: são três colunas em
   * cascata, e reconstruir "Direito Civil > Contratos" a cada volta para a tela
   * é o tipo de trabalho repetido que se paga dezenas de vezes por dia.
   */
  const disciplineId = ler.texto('disciplina');
  const subjectId = ler.texto('assunto');
  // Trocar a disciplina invalida o assunto, que é dela.
  const setDisciplineId = (valor?: string) =>
    filtrar({ disciplina: valor, assunto: undefined });
  const setSubjectId = (valor?: string) => filtrar({ assunto: valor });
  // Trocar o escopo invalida a cascata inteira, que é de outra árvore.
  const setEscopoId = (valor?: string) =>
    filtrar({ escopo: valor, disciplina: undefined, assunto: undefined });

  // A delimitação herda o número do assunto ("2.1"): pega a posição do assunto
  // selecionado na lista ordenada (mesma chave de cache que a coluna usa).
  const assuntos = useQuery({
    queryKey: ['subjects', disciplineId, ''],
    queryFn: () => api.listSubjects(disciplineId!, undefined),
    enabled: Boolean(disciplineId),
  });
  const posicaoDoAssunto = (assuntos.data ?? []).findIndex((item) => item.id === subjectId);
  const prefixoDaDelimitacao = posicaoDoAssunto >= 0 ? `${posicaoDoAssunto + 1}.` : '';

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="Renomeie pelo lápis e arraste pela alça para ordenar. Para excluir, vá de baixo para cima: primeiro a delimitação, depois o assunto e por último a disciplina."
      />

      <Space align="center" size={8} style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary">Concurso ou catálogo</Typography.Text>
        <Select
          style={{ width: 220 }}
          value={escopoId}
          loading={escopos.isPending}
          onChange={setEscopoId}
          options={(escopos.data ?? []).map((escopo) => ({
            value: escopo.id,
            label: escopo.name,
          }))}
        />
      </Space>

      {/* Proporção 2:3:3 no desktop: disciplina é uma palavra ou duas, mas
          assunto e delimitação são textos longos e precisam de mais espaço.
          No celular (xs) cada card ocupa a linha inteira. */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6}>
          <Card size="small" title="Disciplinas">
            <ListaSimples
              titulo="Disciplina"
              queryKey={['disciplines', escopoId]}
              desabilitado={!escopoId}
              vazio="Nenhuma disciplina neste escopo."
              selecionado={disciplineId}
              // `setDisciplineId` já limpa o assunto: duas escritas seguidas na
              // URL na mesma renderização se sobrescreveriam.
              onSelecionar={setDisciplineId}
              buscar={(termo) => api.listDisciplines({ scopeId: escopoId, termo })}
              criar={(nome) => api.createDiscipline(escopoId!, nome)}
              renomear={(id, nome) => api.renameDiscipline(id, nome)}
              excluir={(id) => api.deleteDiscipline(id)}
            />
          </Card>
        </Col>

        <Col xs={24} lg={9}>
          <Card size="small" title="Assuntos">
            <ListaSimples
              titulo="Assunto"
              queryKey={['subjects', disciplineId]}
              desabilitado={!disciplineId}
              numerada
              vazio={disciplineId ? 'Nenhum assunto nesta disciplina.' : 'Escolha uma disciplina.'}
              selecionado={subjectId}
              onSelecionar={setSubjectId}
              buscar={(termo) => api.listSubjects(disciplineId!, termo)}
              criar={(nome) => api.createSubject(disciplineId!, nome)}
              renomear={(id, nome) => api.renameSubject(id, nome)}
              excluir={(id) => api.deleteSubject(id)}
              onReordenar={(ids) => api.reorderSubjects(disciplineId!, ids)}
            />
          </Card>
        </Col>

        <Col xs={24} lg={9}>
          <Card size="small" title="Delimitações">
            <ListaSimples
              titulo="Delimitação"
              queryKey={['subject-details', subjectId]}
              desabilitado={!subjectId}
              numerada
              prefixoNumero={prefixoDaDelimitacao}
              vazio={subjectId ? 'Nenhuma delimitação neste assunto.' : 'Escolha um assunto.'}
              buscar={(termo) => api.listSubjectDetails(subjectId!, termo)}
              criar={(nome) => api.createSubjectDetail(subjectId!, nome)}
              renomear={(id, nome) => api.renameSubjectDetail(id, nome)}
              excluir={(id) => api.deleteSubjectDetail(id)}
              onReordenar={(ids) => api.reorderSubjectDetails(subjectId!, ids)}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}
